import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const CHANGE_SLIDE_MS = 2500
const TRANSITION_ANIMATION_MS = 500

const CardWrapper = styled(Group)`
  width: 100%;
  overflow: hidden;
`

const CardsContainer = styled(Group)`
  width: 100%;
  position: relative;
  top: 0;
  left: 0;
  transition: ease left ${TRANSITION_ANIMATION_MS}ms;
`

const CardContainer = styled(Group)`
  min-width: 100%;
  width: 100%;
  padding: ${rem(80)} 0;
`

interface LoadingSlide {
  title: React.ReactNode | string
  description: string
  image: string
}

type LoadingVariant = 'flights' | 'hotels'

interface Props {
  variant?: LoadingVariant
}

export default function LoadingAnimationBox({ variant = 'flights' }: Props) {
  const intl = useIntl()
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)

  const allSlides = useMemo((): Array<LoadingSlide> => {
    const programName = getLoyaltyProgramDisplayName()

    const flightSlides: Array<LoadingSlide> = [
      {
        image: '2h5x3bxf489ge3vbhowhv',
        title: <FormattedMessage
          id="55qbenH1W6LTKl7rRhCJ"
          defaultMessage="Finding the <i>best</i> prices & airlines"
          description="Loading animation slide title for flight search - best prices and airlines"
          values={{ i: (chunks) => <i>{chunks}</i> }}
        />,
        description: intl.formatMessage({
          id: 'b00Z_5GB46hrgjl3z7_0',
          defaultMessage: 'Discover unbeatable fares and top-rated airlines.',
          description: 'Loading animation slide description for flight search - fares and airlines',
        }),
      },
      {
        image: '86ukrd82g6wr0qvj1si',
        title: intl.formatMessage({
          id: 'ZCDb4sn0D1fixb5PifZt',
          defaultMessage: 'Our team are available 24/7',
          description: 'Loading animation slide title about customer support availability',
        }),
        description: intl.formatMessage({
          id: 'PAYaaBK8l0HmQ-Kv84ij',
          defaultMessage: 'We’re here for you, anytime, anywhere.',
          description: 'Loading animation slide description about customer support',
        }),
      },
      {
        image: '783xjama1tn1w5kqcc',
        title: <FormattedMessage
          id="OpXQjZgXpn-_jV9yFZFt"
          defaultMessage="Book <i>all</i> your travel in one place"
          description="Loading animation slide title about booking all travel together"
          values={{ i: (chunks) => <i>{chunks}</i> }}
        />,
        description: intl.formatMessage({
          id: 'P8LwTvO34pc7uLE59TLN',
          defaultMessage: 'One-stop solution for flights, hotels, and more.',
          description: 'Loading animation slide description about one-stop travel booking',
        }),
      },
    ]

    const hotelSlides: Array<LoadingSlide> = [
      {
        image: 'kfeeuohiqsojdk1jpx5',
        title: <FormattedMessage
          id="Qtt3Qvp6huYcTLW-Idt7"
          defaultMessage="Finding the <i>best</i> prices & stays"
          description="Loading animation slide title for hotel search - best prices and stays"
          values={{ i: (chunks) => <i>{chunks}</i> }}
        />,
        description: intl.formatMessage({
          id: 'hZo2T_WTFiLeAcxTVFsx',
          defaultMessage: 'Discover unbeatable experiences and top-rated hotels.',
          description: 'Loading animation slide description for hotel search - experiences and hotels',
        }),
      },
      {
        image: '86ukrd82g6wr0qvj1si',
        title: intl.formatMessage({
          id: 'ZCDb4sn0D1fixb5PifZt',
          defaultMessage: 'Our team are available 24/7',
          description: 'Loading animation slide title about customer support availability',
        }),
        description: intl.formatMessage({
          id: 'PAYaaBK8l0HmQ-Kv84ij',
          defaultMessage: 'We’re here for you, anytime, anywhere.',
          description: 'Loading animation slide description about customer support',
        }),
      },
      {
        image: '783xjama1tn1w5kqcc',
        title: <FormattedMessage
          id="OpXQjZgXpn-_jV9yFZFt"
          defaultMessage="Book <i>all</i> your travel in one place"
          description="Loading animation slide title about booking all travel together"
          values={{ i: (chunks) => <i>{chunks}</i> }}
        />,
        description: intl.formatMessage({
          id: 'P8LwTvO34pc7uLE59TLN',
          defaultMessage: 'One-stop solution for flights, hotels, and more.',
          description: 'Loading animation slide description about one-stop travel booking',
        }),
      },
    ]

    const loyaltySlide: LoadingSlide = {
      image: 'le-loyalty-coins',
      title: <FormattedMessage
        id="hF5RsgpXaXIE-yAxWayR"
        defaultMessage="Earn <i>{programName}</i> points & status credits"
        description="Loading animation slide title about earning loyalty program points"
        values={{
          i: (chunks) => <i>{chunks}</i>,
          programName,
        }}
      />,
      description: intl.formatMessage({
        id: 'XJEK65A4b8lXabInYjol',
        defaultMessage: 'Get rewarded for every flight you book',
        description: 'Loading animation slide description about loyalty rewards for flights',
      }),
    }

    const displaySlides = variant === 'flights' ? flightSlides : hotelSlides
    const slides = [...displaySlides, displaySlides[0]]

    if (variant === 'flights' && isLoyaltyEnabled) {
      slides.splice(1, 0, loyaltySlide)
    }

    return slides
  }, [intl, variant, isLoyaltyEnabled])

  const [activeIndex, setActiveIndex] = useState(0)
  const cardsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => {
        return prevIndex === allSlides.length - 1 ? 0 : prevIndex + 1
      })
    }, CHANGE_SLIDE_MS)

    return () => clearInterval(interval)
  }, [allSlides.length])

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (!cardsRef.current) return

      if (activeIndex === allSlides.length - 1) {
        cardsRef.current.style.transition = 'none'
      } else {
        cardsRef.current.style.transition = `ease left ${TRANSITION_ANIMATION_MS}ms`
      }
    }, TRANSITION_ANIMATION_MS)

    return () => {
      clearTimeout(timerId)
    }
  }, [activeIndex, allSlides.length])

  return (
    <CardWrapper direction="horizontal">
      <CardsContainer
        ref={cardsRef}
        direction="horizontal"
        style={{ left: `${activeIndex * -100}%` }}
      >
        {allSlides.map((slide, index) => (
          <CardContainer
            key={index}
            direction="vertical"
            verticalAlign="center"
            horizontalAlign="center"
            gap="24 0"
          >
            <Image width="120px" height="120px" id={slide.image} enhancedFormats={false} />

            <VerticalSpacer gap={12}>
              <HeadingTextBlock variant="heading3" align="center">
                {slide.title}
              </HeadingTextBlock>

              <BodyTextBlock variant="medium" align="center">
                {slide.description}
              </BodyTextBlock>
            </VerticalSpacer>
          </CardContainer>
        ))}
      </CardsContainer>
    </CardWrapper>
  )
}
